import React, { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Badge,
  Tag,
  Descriptions,
  Divider,
  Space,
  Spin,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  TableOutlined,
  ClockCircleOutlined,
  UserOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { Schedule } from "../../hooks/useSchedule";
import {
  getBadgeStatus,
  capitalize,
  getShiftColor,
} from "../../helpers/schedule";
import { useTables } from "../../hooks/useTables";

interface ScheduleViewModalProps {
  visible: boolean;
  onCancel: () => void;
  onEdit: (schedule: Schedule) => void;
  onDelete: (id: number) => void;
  schedule: Schedule | null;
  isDeleting: boolean;
  canModifySchedules: boolean;
  canDeleteSchedules: boolean;
}

const ScheduleViewModal: React.FC<ScheduleViewModalProps> = ({
  visible,
  onCancel,
  onEdit,
  onDelete,
  schedule,
  isDeleting,
  canModifySchedules,
  canDeleteSchedules,
}) => {
  const [tableNumber, setTableNumber] = useState<string | undefined>(undefined);
  const [loadingTable, setLoadingTable] = useState<boolean>(false);

  // Fetch table data if needed
  const { data: tablesData } = useTables(1, {
    ...(schedule?.table_id ? { id: schedule.table_id } : {}),
  });

  useEffect(() => {
    if (schedule?.table_id && tablesData?.data) {
      setLoadingTable(true);
      const tableInfo = tablesData.data.find((t) => t.id === schedule.table_id);
      setTableNumber(tableInfo?.number);
      setLoadingTable(false);
    } else {
      setTableNumber(undefined);
    }
  }, [schedule, tablesData]);

  if (!schedule) return null;

  // Prepare footer buttons based on permissions
  const footerButtons = [];

  if (canModifySchedules) {
    footerButtons.push(
      <Button
        key="edit"
        type="primary"
        icon={<EditOutlined />}
        onClick={() => onEdit(schedule)}
      >
        Edit
      </Button>
    );
  }

  if (canDeleteSchedules) {
    footerButtons.push(
      <Button
        key="delete"
        danger
        icon={<DeleteOutlined />}
        onClick={() => onDelete(schedule.id)}
        loading={isDeleting}
      >
        Delete
      </Button>
    );
  }

  footerButtons.push(
    <Button key="close" onClick={onCancel}>
      Close
    </Button>
  );

  return (
    <Modal
      title={
        <div className="flex items-center">
          <span className="text-xl">Schedule Details</span>
          <Tag color={getShiftColor(schedule.shift_type)} className="ml-3">
            {capitalize(schedule.shift_type)} Shift
          </Tag>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      width={600}
      footer={footerButtons}
    >
      <Descriptions
        bordered
        column={1}
        labelStyle={{ fontWeight: "bold", width: "120px" }}
        className="mb-4"
      >
        <Descriptions.Item
          label={
            <Space>
              <UserOutlined /> Staff
            </Space>
          }
        >
          {schedule.user.name}
        </Descriptions.Item>

        <Descriptions.Item
          label={
            <Space>
              <EnvironmentOutlined /> Location
            </Space>
          }
        >
          {schedule.user.location.name}
        </Descriptions.Item>

        {schedule.table_id && (
          <Descriptions.Item
            label={
              <Space>
                <TableOutlined /> Table
              </Space>
            }
          >
            {loadingTable ? (
              <Spin size="small" />
            ) : (
              <Tag color="blue" icon={<TableOutlined />}>
                Table #{tableNumber || "Unknown"}
              </Tag>
            )}
          </Descriptions.Item>
        )}

        <Descriptions.Item
          label={
            <Space>
              <CalendarOutlined /> Date
            </Space>
          }
        >
          {dayjs(schedule.schedule_date).format("MMMM D, YYYY")}
        </Descriptions.Item>

        <Descriptions.Item
          label={
            <Space>
              <ClockCircleOutlined /> Time
            </Space>
          }
        >
          {dayjs(schedule.start_time, "HH:mm:ss").format("h:mm A")} -{" "}
          {dayjs(schedule.end_time, "HH:mm:ss").format("h:mm A")}
        </Descriptions.Item>

        <Descriptions.Item label="Status">
          <Badge
            status={getBadgeStatus(schedule.status)}
            text={capitalize(schedule.status)}
          />
        </Descriptions.Item>
      </Descriptions>

      {schedule.notes && (
        <>
          <Divider orientation="left">
            <Space>
              <FileTextOutlined /> Notes
            </Space>
          </Divider>
          <div className="bg-gray-50 p-3 rounded-lg border-l-4 border-l-blue-400">
            {schedule.notes}
          </div>
        </>
      )}
    </Modal>
  );
};

export default ScheduleViewModal;
